import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { CodeInputContainer, CodeInputMask, FocusBorder, StyleCodeInputItem } from './styles';

const allowedCharactersValues = ['alpha', 'numeric', 'alphanumeric'] as const;

export type AuthCodeProps = {
  allowedCharacters?: (typeof allowedCharactersValues)[number];
  ariaLabel?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  isPassword?: boolean;
  length?: number;
  placeholder?: string;
  error: string;
  onChange: (res: string) => void;
  code?: string;
};

type InputMode = 'text' | 'numeric';

type InputType = 'text' | 'tel' | 'password';

type InputProps = {
  type: InputType;
  inputMode: InputMode;
  pattern: string;
  min?: string;
  max?: string;
};

export type AuthCodeRef = {
  focus: () => void;
  clear: () => void;
};

const propsMap: { [key: string]: InputProps } = {
  alpha: {
    type: 'text',
    inputMode: 'text',
    pattern: '[a-zA-Z]{1}',
  },

  alphanumeric: {
    type: 'text',
    inputMode: 'text',
    pattern: '[a-zA-Z0-9]{1}',
  },

  numeric: {
    type: 'tel',
    inputMode: 'numeric',
    pattern: '[0-9]{1}',
    min: '0',
    max: '9',
  },
};

const CodeInput = forwardRef<AuthCodeRef, AuthCodeProps>(
  (
    {
      allowedCharacters = 'alphanumeric',
      ariaLabel,
      autoFocus = true,
      disabled,
      isPassword = false,
      length = 6,
      placeholder,
      onChange,
      code = '',
      error = '',
    },
    ref
  ) => {
    if (isNaN(length) || length < 1) {
      throw new Error('Length should be a number and greater than 0');
    }

    if (!allowedCharactersValues.some((value) => value === allowedCharacters)) {
      throw new Error('Invalid value for allowedCharacters. Use alpha, numeric, or alphanumeric');
    }

    const [focusIndex, setFocusIndex] = React.useState<number>(0);
    const [showFocusBorder, setShowFocusBorder] = React.useState<boolean>(false);

    const inputsRef = useRef<Array<HTMLInputElement>>([]);
    const inputProps = propsMap[allowedCharacters] as InputProps;
    const codeInputContainerRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(ref, () => ({
      focus: () => {
        if (inputsRef.current) {
          inputsRef.current[0]?.focus();
        }
      },
      clear: () => {
        if (inputsRef.current) {
          for (let i = 0; i < inputsRef.current.length; i++) {
            if (inputsRef.current?.[i]) {
              (inputsRef.current[i] as HTMLInputElement).value = '';
            }
          }
          (inputsRef.current[0] as HTMLInputElement).focus();
        }
        sendResult();
      },
    }));

    // useEffect(() => {
    //   if (autoFocus) {
    //     (inputsRef.current[0] as HTMLInputElement).focus();
    //   }
    // }, []);

    useEffect(() => {
      if (code) {
        for (let i = 0; i < inputsRef.current.length; i++) {
          (inputsRef.current[i] as HTMLInputElement).value = '';
        }
        for (let i = 0; i < code.length; i++) {
          if (inputsRef.current?.[i]) {
            (inputsRef.current[i] as HTMLInputElement).value = code[i] || '';
          }
        }
        sendResult();
      }
    }, [code]);

    let oldValue = inputsRef.current.map((input) => input.value).join('') || '';

    const sendResult = () => {
      const res = inputsRef.current.map((input) => input.value).join('');
      if (oldValue !== res) {
        console.log('sendResult', res);

        if (res.length === 6) {
          setTimeout(() => {
            inputsRef.current.forEach((input) => {
              input.blur();
            });
          }, 400);
        }

        onChange && onChange(res);
        oldValue = res;
      }
    };

    const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const {
        target: { value, nextElementSibling },
      } = e;

      if (value.length == 1) {
        if (value.match(inputProps.pattern)) {
          if (nextElementSibling !== null) {
            (nextElementSibling as HTMLInputElement).focus();
          }
        } else {
          e.target.value = '';
        }
      } else if (value.length > 1) {
        const pastedValue = value;
        let currentInput: number = Number(e.target.getAttribute('data-index')) || 0;
        for (let i = 0; i < pastedValue.length; i++) {
          const pastedCharacter = pastedValue.charAt(i);
          if (pastedCharacter.match(inputProps.pattern) && inputsRef.current?.[currentInput]) {
            (inputsRef.current[currentInput] as HTMLInputElement).value = pastedCharacter;
            if ((inputsRef.current[currentInput] as HTMLInputElement).nextElementSibling !== null) {
              ((inputsRef.current[currentInput] as HTMLInputElement).nextElementSibling as HTMLInputElement).focus();
              currentInput++;
            }
          }
        }
      }
      setTimeout(() => {
        sendResult();
      });
    };

    const handleOnKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      const { key } = e;
      const target = e.target as HTMLInputElement;
      if (key === 'Backspace') {
        if (target.value === '') {
          if (target.previousElementSibling !== null) {
            const t = target.previousElementSibling as HTMLInputElement;
            t.value = '';
            t.focus();
            e.preventDefault();
          }
        } else {
          target.value = '';
        }
        setTimeout(() => {
          sendResult();
        });
      }
    };

    const handleOnFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      e.target.select();
    };

    const handleOnPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
      let pastedValue = e.clipboardData.getData('Text') ?? '';
      pastedValue = pastedValue.replace(/\s/g, '').substring(0, 6);

      let currentInputIndex = focusIndex;

      for (let i = 0; i < pastedValue.length; i++) {
        const pastedCharacter = pastedValue.charAt(i);
        const currentInput = inputsRef.current[currentInputIndex] as HTMLInputElement;
        if (!currentInput) {
          break;
        }
        const currentValue = currentInput?.value;
        if (pastedCharacter.match(inputProps.pattern)) {
          if (!currentValue) {
            (inputsRef.current[currentInputIndex] as HTMLInputElement).value = pastedCharacter;
            if ((inputsRef.current[currentInputIndex] as HTMLInputElement).nextElementSibling !== null) {
              (
                (inputsRef.current[currentInputIndex] as HTMLInputElement).nextElementSibling as HTMLInputElement
              ).focus();
              currentInputIndex++;
            }
          }
        }
      }
      sendResult();

      e.preventDefault();
    };

    return (
      <CodeInputContainer ref={codeInputContainerRef}>
        <FocusBorder $focusIndex={focusIndex} $showFocusBorder={showFocusBorder} />
        {new Array(6).fill(0).map((_, i) => {
          return (
            <StyleCodeInputItem
              key={i}
              data-index={i}
              onChange={handleOnChange}
              onKeyDown={handleOnKeyDown}
              onFocus={(e) => {
                setShowFocusBorder(true);
                setFocusIndex(i);
                handleOnFocus(e);
              }}
              autoFocus={autoFocus && i === 0}
              onBlur={() => {
                setShowFocusBorder(false);
              }}
              onPaste={handleOnPaste}
              {...inputProps}
              type={isPassword ? 'password' : inputProps.type}
              ref={(el: HTMLInputElement) => {
                inputsRef.current[i] = el;
              }}
              autoComplete={i === 0 ? 'one-time-code' : 'off'}
              aria-label={ariaLabel ? `${ariaLabel}. Character ${i + 1}.` : `Character ${i + 1}.`}
              disabled={disabled}
              placeholder={placeholder}
              error={error}
            />
          );
        })}
        <CodeInputMask
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            for (let i = inputsRef.current.length - 1; i >= 0; i--) {
              if (
                (i > 0 &&
                  !(inputsRef.current[i] as HTMLInputElement).value &&
                  (inputsRef.current[i - 1] as HTMLInputElement).value) ||
                (i === 0 && !(inputsRef.current[i] as HTMLInputElement).value) ||
                (i == inputsRef.current.length - 1 && (inputsRef.current[i] as HTMLInputElement).value)
              ) {
                (inputsRef.current[i] as HTMLInputElement).focus();
                (inputsRef.current[i] as HTMLInputElement).setSelectionRange(
                  (inputsRef.current[i] as HTMLInputElement).value.length,
                  (inputsRef.current[i] as HTMLInputElement).value.length
                );
                break;
              }
            }
          }}
        />
      </CodeInputContainer>
    );
  }
);

export default CodeInput;
